/**
 * Service Registry - Central service factory for test mode mocking
 *
 * This registry pattern isolates test mode logic from production code.
 * Services are registered once with both real and mock implementations.
 * The registry automatically returns the correct implementation based on test mode.
 *
 * Benefits:
 * - Production services have ZERO test logic
 * - Mock logic isolated in separate files
 * - Single source of truth for service instantiation
 * - Type-safe service resolution
 */

import { isTestMode } from 'src/config/env'

type ServiceClass<T> = new () => T
type ServiceConfig<T> = {
  real: ServiceClass<T>
  mock: ServiceClass<T>
}

// Internal service registry map
const services = new Map<string, { real: any; mock: any; instance?: any }>()

/**
 * Register a service with both real and mock implementations
 *
 * @param key - Unique service identifier
 * @param real - Real service class constructor
 * @param mock - Mock service class constructor
 *
 * @example
 * registerService('account', AccountService, MockAccountService)
 */
export function registerService<T>(key: string, real: any, mock: any): void {
  services.set(key, { real, mock })
}

/**
 * Get a service instance (singleton pattern)
 * Automatically returns mock or real implementation based on test mode
 *
 * @param key - Service identifier (must be registered first)
 * @returns Service instance (cached)
 *
 * @throws Error if service not registered
 *
 * @example
 * const accountService = getService<IAccountService>('account')
 */
export function getService<T>(key: string): T {
  const config = services.get(key)

  if (!config) {
    throw new Error(
      `Service '${key}' not registered. ` +
      `Available services: ${Array.from(services.keys()).join(', ')}`
    )
  }

  // Lazy instantiation with singleton pattern
  if (!config.instance) {
    // isTestMode() check happens ONLY here - not in service implementations
    const testMode = isTestMode()
    console.log(`[ServiceRegistry] getService('${key}'): isTestMode() = ${testMode}`)

    const ServiceClass = testMode ? config.mock : config.real
    config.instance = new ServiceClass()

    if (testMode) {
      console.log(`[ServiceRegistry] Using MOCK service: ${key}`)
    } else {
      console.log(`[ServiceRegistry] Using REAL service: ${key}`)
    }
  } else {
    console.log(`[ServiceRegistry] Returning cached service: ${key}`)
  }

  return config.instance
}

/**
 * Reset all service instances (useful for testing)
 * Forces re-instantiation on next getService() call
 */
export function resetAll(): void {
  services.clear()
}

/**
 * Get list of registered service keys (for debugging)
 */
export function getRegisteredServices(): string[] {
  return Array.from(services.keys())
}
